import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useApiQuery } from '../../api/useApiQuery.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.jsx';
import { MenuQrCode } from '../../components/MenuQrCode.jsx';
import { getPublicMenuUrl } from '../../utils/publicMenuUrl.js';
import { useTenantAdminAuth } from '../../auth/TenantAdminAuthContext.jsx';
import { Icon } from '../../components/Icon.jsx';
import { Modal } from '../../components/Modal.jsx';

export function MenusListPage() {
  const { identity } = useTenantAdminAuth();
  const { data: menus, loading, error, reload } = useApiQuery(() => api.get('/api/menus'), []);
  const { data: menuUsage, error: menuUsageError, reload: reloadMenuUsage } = useApiQuery(() => api.get('/api/menus/limits'), []);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [useStarterTemplate, setUseStarterTemplate] = useState(true);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState(null);
  const navigate = useNavigate();
  const menuItems = menus ?? [];

  async function handleCreate(event) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const menu = await api.post('/api/menus', { name, description: description.trim() || undefined, useStarterTemplate });
      navigate(`/menus/${menu.id}`);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive() {
    setArchiving(true);
    setArchiveError(null);
    try {
      await api.delete(`/api/menus/${archiveTarget.id}`);
      setArchiveTarget(null);
      reload();
      reloadMenuUsage();
    } catch (err) {
      setArchiveError(err.message);
    } finally {
      setArchiving(false);
    }
  }

  const atLimit = menuUsage ? !menuUsage.canCreate : false;

  return (
    <div className="page menus-page">
      <div className="page-header">
        <div><h1>Menus</h1><p>Create, update, and share your restaurant’s menus.</p></div>
        <button type="button" className="btn btn-primary" disabled={atLimit} onClick={() => setCreating((v) => !v)}>
          <Icon name="plus" size={17} /> Create menu
        </button>
      </div>

      {menuUsage && (
        <div className={`tenant-menu-capacity ${atLimit ? 'tenant-menu-capacity--full' : ''}`}>
          <strong>{menuUsage.used} of {menuUsage.limit} menus used</strong>
          <span>{atLimit ? 'You have reached your menu limit. Archive a menu or contact your platform administrator to increase it.' : `${menuUsage.remaining} menu slot${menuUsage.remaining === 1 ? '' : 's'} available.`}</span>
        </div>
      )}
      {menuUsageError && <p className="field-error">Could not load your menu limit. You can still manage existing menus.</p>}

      {creating && (
        <Modal onClose={() => setCreating(false)} labelledBy="create-menu-title" busy={submitting}>
          <h2 id="create-menu-title">Create a menu</h2>
          <p className="modal-description">Start fresh or create a complete sample menu you can customize.</p>
          <form onSubmit={handleCreate}>
            {formError && <p className="field-error">{formError}</p>}
            <div className="field">
              <label htmlFor="menu-name">Menu name</label>
              <input
                id="menu-name"
                type="text"
                required
                placeholder="e.g. Main Menu, Breakfast, Drinks"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <span className="field-hint">Its web address will be generated from this name automatically.</span>
            </div>
            <div className="field">
              <label htmlFor="menu-description">Menu description <span className="optional-label">Optional</span></label>
              <textarea
                id="menu-description"
                rows="3"
                maxLength={2000}
                placeholder="A short introduction for your guests."
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <span className="field-hint">Sample menus include a starter description, which you can replace here.</span>
            </div>
            <label className="starter-template-choice">
              <input type="checkbox" checked={useStarterTemplate} onChange={(event) => setUseStarterTemplate(event.target.checked)} />
              <span>
                <strong>Start with sample sections and dishes</strong>
                <small>Adds photographed dishes, dietary details, customer choices, a logo, cover image, and ready-to-edit design settings. You can change or remove everything.</small>
              </span>
            </label>
            <div className="dialog-actions">
              <button type="button" className="btn" disabled={submitting} onClick={() => setCreating(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || atLimit}>
                {submitting ? 'Creating…' : 'Create menu'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {error && <p className="field-error">{error.message}</p>}

      {loading ? (
        <span className="spinner" aria-label="Loading" />
      ) : error ? null : menuItems.length === 0 ? (
        <div className="empty">
          <strong>You don't have any menus yet</strong>
          <p>Create your first menu to start adding categories and items.</p>
        </div>
      ) : (
        <div className="list">
          {menuItems.map((menu) => (
            <div key={menu.id} className="list-row">
              <div className="menu-list__identity">
                <span className="menu-list__icon"><Icon name="menu" size={21} /></span>
                <div>
                  <Link to={`/menus/${menu.id}`} style={{ fontWeight: 600, textDecoration: 'none', color: 'inherit' }}>
                    {menu.name}
                  </Link>
                  <div className="field-hint">
                    {menu.isPublished ? (
                      <span className="badge badge-success">Published</span>
                    ) : (
                      <span className="badge badge-muted">Draft</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="menu-list__actions">
                {menu.isPublished && identity?.tenantSlug && <MenuQrCode menuName={menu.name} menuSlug={menu.slug} menuUrl={getPublicMenuUrl(identity.tenantSlug, menu.slug)} />}
                <Link to={`/menus/${menu.id}`} className="btn btn-sm" aria-label={`Edit ${menu.name}`}>
                  Edit
                </Link>
                <button type="button" className="btn btn-sm btn-danger" onClick={() => { setArchiveError(null); setArchiveTarget(menu); }} aria-label={`Archive ${menu.name}`}>
                  Archive
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {archiveTarget && (
        <ConfirmDialog
          title={`Archive "${archiveTarget.name}"?`}
          description="This removes it from your menu list. Already-published content stays visible to customers until you unpublish it separately."
          confirmLabel="Archive"
          busy={archiving}
          error={archiveError}
          onConfirm={handleArchive}
          onCancel={() => setArchiveTarget(null)}
        />
      )}
    </div>
  );
}
